import {effectiveReportingRange, snapshotUnavailableShort} from "../../utils/reportingSnapshotAvailability.js";
import React, {useContext, useEffect, useMemo, useState} from "react";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import OrderDashboardFilters from "../dashboard/OrderDashboard/Layout/OrderDashboardFilters.jsx";
import {FilterContext} from "../../components/filters/DashboardFilterContextProvider.jsx";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {ErpCalculationApi} from "../../api/authority/generated/index";
import {useReportingError} from "../../api/utilities/useReportingError.js";
import toast from "react-hot-toast";
import EChart from "./ReportingEChart.jsx";
import Spinner from "../../components/Spinner.jsx";
import Tooltip from "../../components/Tooltip.jsx";
import {useRenderCurrencyValue, useRenderNumber, useRenderPercentValue} from "../../api/utilities/useValueRenderers.js";
import {useTranslation} from "react-i18next";
import {reportingSnapshotCompletenessAlert} from "../../utils/reportingSnapshotCompleteness.js";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronDown, faChevronLeft, faChevronRight, faChevronUp, faSort} from "@fortawesome/free-solid-svg-icons";
import ModernSearchInput from "../../components/ModernSearchInput.jsx";
import ReportingInfoTooltip from "../../components/ReportingInfoTooltip.jsx";
import {hasReturnedMoneyMetric, purchaseCostQuality} from "../../utils/reportingFinancialCompleteness.js";
import {calculatePnoPercent, divideRatio, formatRatioMultiplier, nullableMoneyMajorUnitAmount} from "../../utils/reportingMetricRatios.js";
import {isSnapshotPeriodIncomplete, mediaSourceQualityBlocksMetrics, snapshotDateValue, snapshotPeriodValue, snapshotHasPublishedDays, snapshotPublicationDisplay, withSnapshotMissingDays} from "../../utils/reportingSnapshotAvailability.js";
import ReportingSnapshotStatus from "./ReportingSnapshotStatus.jsx";
import {partialProfitabilityChartPoint, partialProfitabilityMarginPercent, selectProfitabilityMetric} from "../../utils/reportingPartialProfitability.js";
import ReportingCostCoverageContext from "../../components/reporting/ReportingCostCoverageContext.jsx";
import {selectedCentreSpendAttributionStatus} from "./CentreSpendAttributionNotice.jsx";
import CentreSpendAttributionNotice from "./CentreSpendAttributionNotice.jsx";

export const ChannelMixColors = ["#2563eb", "#16a34a", "#f97316", "#7c3aed", "#0891b2", "#db2777", "#65a30d", "#475569"];
export const RevenueTrendColors = ["#2563eb", "#16a34a", "#f97316"];
export const MetricBasis = {
    Marketing: "marketing",
    Completed: "completed",
};

export function overviewMetricBasisData(shopPerformance, metricBasis) {
    const isMarketing = metricBasis === MetricBasis.Marketing;

    return {
        partialProfitability: shopPerformance?.[isMarketing ? "partialProfitabilityMarketing" : "partialProfitability"],
        partialProfitabilityGranular: shopPerformance?.[isMarketing ? "partialProfitabilityMarketingGranular" : "partialProfitabilityGranular"],
        purchaseCostUnknownGranular: shopPerformance?.[isMarketing ? "purchaseCostUnknownGrossGranular" : "purchaseCostUnknownNetGranular"],
        reportContext: isMarketing ? "marketing" : "overview",
    };
}

export function moneyAmount(money) {
    return Number(money?.amount ?? 0);
}

export function moneyToMajor(money) {
    return moneyAmount(money) / 100;
}

/** Presentation only: keep the stored subtotal for calculations, but do not imply a known zero when source quality is incomplete. */
export function channelSpendPresentation(row) {
    const spend = row?.spendMarketing;
    if (!mediaSourceQualityBlocksMetrics(row?.mediaSourceQuality)) {
        return {value: spend, partial: false};
    }

    if (spend?.amount == null) {
        return {value: null, partial: false};
    }

    if (Number(spend.amount) === 0 && row?.hasObservedMediaSpendContribution !== true) {
        return {value: null, partial: false};
    }

    return {value: spend, partial: true};
}

export function channelSpendChartPoint(row) {
    const spend = channelSpendPresentation(row).value;
    return spend == null ? null : moneyToMajor(spend);
}

/** Suppresses only the duplicate source-quality notice when publication explicitly proves no days are available. */
export function snapshotStatusMediaSourceQuality(snapshot, mediaSourceQuality) {
    const publishedDays = snapshotPublicationDisplay(snapshot).publishedDays;
    return Array.isArray(publishedDays) && publishedDays.length === 0
        ? {applicable: false, available: true, complete: true, issues: []}
        : mediaSourceQuality;
}

/** Defers dispatch by one microtask so a development StrictMode replay can cancel before backend work starts. */
export function startCancelableReportingRequest(request, {onSuccess, onError, onFinally}) {
    const controller = new AbortController();
    let active = true;
    let dispatched = false;

    Promise.resolve()
        .then(() => {
            if (!active) return undefined;
            dispatched = true;
            return request(controller.signal);
        })
        .then((result) => {
            if (active && dispatched) onSuccess(result);
        })
        .catch((error) => {
            if (active && dispatched) onError(error);
        })
        .finally(() => {
            if (active && dispatched) onFinally?.();
        });

    return () => {
        active = false;
        controller.abort();
    };
}

export function dividePercent(numerator, denominator) {
    if (!denominator) {
        return null;
    }

    return (numerator / denominator) * 100;
}

export {divideRatio};

export function formatDateLabel(value) {
    if (typeof value === "string") {
        const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
        if (dateOnly) {
            return `${Number(dateOnly[3])}.${Number(dateOnly[2])}.${dateOnly[1]}`;
        }
    }

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
        return String(value ?? "");
    }

    const parts = Object.fromEntries(new Intl.DateTimeFormat("cs-CZ", {
        timeZone: "Europe/Prague",
        day: "numeric",
        month: "numeric",
        year: "numeric",
    }).formatToParts(date).map(({type, value: partValue}) => [type, partValue]));

    return `${parts.day}.${parts.month}.${parts.year}`;
}

export function formatDateTimeLabel(value, includeSeconds = true) {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value ? String(value) : "—";
    }

    const parts = Object.fromEntries(new Intl.DateTimeFormat("cs-CZ", {
        timeZone: "Europe/Prague",
        day: "numeric",
        month: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23",
    }).formatToParts(date).map(({type, value: partValue}) => [type, partValue]));

    const datePart = `${parts.day}.${parts.month}.${parts.year}`;
    const timePart = includeSeconds
        ? `${parts.hour}:${parts.minute}:${parts.second}`
        : `${parts.hour}:${parts.minute}`;

    return `${datePart} ${timePart}`;
}

export function createEmptyMoney(currency = null) {
    return {amount: 0, currency};
}

export function projectCurrency(project) {
    return project?.currency ?? project?.currencyCode ?? null;
}

export function currencyUnit(currency = null) {
    if (!currency) {
        return "";
    }

    const units = {
        CZK: "Kč",
        EUR: "€",
        USD: "$",
        GBP: "£",
    };

    return units[currency] ?? currency;
}

export function formatChartNumber(value, {minimumFractionDigits = 0, maximumFractionDigits = 0} = {}) {
    return new Intl.NumberFormat("cs-CZ", {
        minimumFractionDigits,
        maximumFractionDigits,
    }).format(Number(value ?? 0));
}

export function formatChartCurrency(value, currency = null) {
    const unit = currencyUnit(currency);

    return unit ? `${formatChartNumber(value)} ${unit}` : formatChartNumber(value);
}

export function formatChartPercent(value, decimals = 0) {
    if (value === null || value === undefined) {
        return "—";
    }

    return `${formatChartNumber(value, {maximumFractionDigits: decimals})} %`;
}

export function formatChartMultiplier(value, decimals = 1) {
    return formatRatioMultiplier(value, decimals);
}

export function chartParamValue(param) {
    const value = param?.value;

    if (Array.isArray(value)) {
        return value[value.length - 1];
    }

    if (value && typeof value === "object" && Object.prototype.hasOwnProperty.call(value, "value")) {
        return value.value;
    }

    return value;
}

export function formatAxisTooltip(params, formatters = formatChartNumber) {
    const items = Array.isArray(params) ? params : [params];
    const title = items[0]?.axisValueLabel ?? items[0]?.name ?? "";
    const rows = items.map((item, index) => {
        const formatter = Array.isArray(formatters) ? (formatters[index] ?? formatChartNumber) : formatters;
        const value = chartParamValue(item);

        return `${item.marker} ${item.seriesName}${item.data?.metricNote ? ` · ${item.data.metricNote}` : ""}<span style="float:right;margin-left:16px;font-weight:600">${value === null || value === undefined ? "—" : formatter(value)}</span>`;
    });

    return [title, ...rows].filter(Boolean).join("<br/>");
}

export function formatCurrencyItemTooltip(param, currency = null) {
    return `${param.marker} ${param.name}<span style="float:right;margin-left:16px;font-weight:600">${formatChartCurrency(chartParamValue(param), currency)}</span><br/><span style="color:#64748b">${formatChartPercent(param.percent)}</span>`;
}

export function formatNumberItemTooltip(param) {
    return `${param.marker} ${param.name}<span style="float:right;margin-left:16px;font-weight:600">${formatChartNumber(chartParamValue(param))}</span><br/><span style="color:#64748b">${formatChartPercent(param.percent)}</span>`;
}

export function addMoney(left, right) {
    if (!left && !right) {
        return null;
    }

    const base = left ?? createEmptyMoney(right?.currency);
    return {
        ...base,
        amount: moneyAmount(left) + moneyAmount(right),
    };
}

export function divideMoney(money, divisor) {
    if (!money || !divisor) {
        return null;
    }

    return {
        amount: moneyAmount(money) / divisor,
        currency: money?.currency,
    };
}

export function hasMetricValue(value) {
    return value !== null && value !== undefined;
}

export function buildChannelRow(channel, overview, metricBasis) {
    const adAccounts = overview?.adAccounts ?? [];
    const campaigns = adAccounts.flatMap((account) => account.campaigns ?? []);
    const mediaQualityBlocked = mediaSourceQualityBlocksMetrics(overview?.mediaSourceQuality);
    const profitSourceKey = metricBasis === MetricBasis.Completed ? "profitNet" : "profitGross";
    const goodsSourceKey = metricBasis === MetricBasis.Completed ? "spendGoodsNet" : "spendGoodsGross";
    const hasProfit = campaigns.some((campaign) => campaign[profitSourceKey] !== undefined);
    const hasGoods = campaigns.some((campaign) => hasMetricValue(campaign[goodsSourceKey]));
    const costQuality = purchaseCostQuality(campaigns, metricBasis);
    const total = campaigns.reduce((accumulator, campaign) => {
        accumulator.spendMarketing = addMoney(accumulator.spendMarketing, campaign.spendMarketing);
        accumulator.spendGoodsNet = addMoney(accumulator.spendGoodsNet, campaign.spendGoodsNet);
        accumulator.spendGoodsGross = addMoney(accumulator.spendGoodsGross, campaign.spendGoodsGross);
        accumulator.turnoverNet = addMoney(accumulator.turnoverNet, campaign.turnoverNet);
        accumulator.turnoverGross = addMoney(accumulator.turnoverGross, campaign.turnoverGross);
        accumulator.profitNet = addMoney(accumulator.profitNet, campaign.profitNet);
        accumulator.profitGross = addMoney(accumulator.profitGross, campaign.profitGross);
        accumulator.ordersNet += Number(campaign.ordersNet ?? 0);
        accumulator.ordersGross += Number(campaign.ordersGross ?? 0);
        accumulator.clicks += Number(campaign.clicks ?? 0);
        accumulator.impressions += Number(campaign.impressions ?? 0);
        return accumulator;
    }, {
        channel,
        campaigns,
        accountNames: [...new Set(adAccounts.map((account) => account.name).filter(Boolean))],
        spendMarketing: null,
        spendGoodsNet: null,
        spendGoodsGross: null,
        turnoverNet: null,
        turnoverGross: null,
        profitNet: null,
        profitGross: null,
        ordersNet: 0,
        ordersGross: 0,
        clicks: 0,
        impressions: 0,
    });

    const turnover = metricBasis === MetricBasis.Completed ? total.turnoverNet : total.turnoverGross;
    const profit = metricBasis === MetricBasis.Completed ? total.profitNet : total.profitGross;
    const orders = metricBasis === MetricBasis.Completed ? total.ordersNet : total.ordersGross;
    const goods = metricBasis === MetricBasis.Completed ? total.spendGoodsNet : total.spendGoodsGross;
    const financialMetricsComplete = costQuality.complete && hasProfit && campaigns.every((campaign) => campaign[profitSourceKey] != null);
    const afterAdsMetricsComplete = !mediaQualityBlocked && financialMetricsComplete;
    const partialProfitability = metricBasis === MetricBasis.Completed
        ? overview?.partialProfitability
        : overview?.partialProfitabilityMarketing;
    const afterAdsPartialProfitability = mediaQualityBlocked ? null : partialProfitability;
    const fullGrossProfit = financialMetricsComplete && turnover != null && goods != null ? {
        amount: moneyAmount(turnover) - moneyAmount(goods),
        currency: turnover?.currency ?? goods?.currency ?? total.spendMarketing?.currency,
    } : afterAdsMetricsComplete && profit != null && total.spendMarketing != null ? {
        amount: moneyAmount(profit) + moneyAmount(total.spendMarketing),
        currency: profit.currency ?? total.spendMarketing.currency,
    } : undefined;
    const selectedProfit = selectProfitabilityMetric({fullValue: hasProfit ? profit : undefined, fullComplete: afterAdsMetricsComplete, partialProfitability: afterAdsPartialProfitability, partialField: "knownProfitAfterAllMarketing"});
    const selectedGrossProfit = selectProfitabilityMetric({fullValue: hasProfit ? fullGrossProfit ?? null : undefined, fullComplete: financialMetricsComplete, partialProfitability, partialField: "knownGrossProfit"});
    const selectedPoas = selectProfitabilityMetric({fullValue: hasProfit ? (fullGrossProfit && Number(turnover?.amount) >= 0 ? divideRatio(fullGrossProfit.amount, total.spendMarketing?.amount) : null) : undefined, fullComplete: afterAdsMetricsComplete, partialProfitability: afterAdsPartialProfitability, partialField: "knownPoas"});
    const contributionBeforeMarketing = selectedGrossProfit.value;

    return {
        ...total,
        mediaSourceQuality: overview?.mediaSourceQuality,
        hasObservedMediaSpendContribution: overview?.hasObservedMediaSpendContribution === true,
        purchaseCostQualityAvailable: costQuality.available,
        purchaseCostComplete: costQuality.complete,
        purchaseCostUnknownCount: costQuality.unknownCount,
        purchaseCostEstimatedCount: costQuality.estimatedCount,
        turnover,
        ...(selectedProfit.allowed ? {profit: selectedProfit.value} : {}),
        partialProfitability,
        profitIsPartial: selectedProfit.partial,
        grossProfitIsPartial: selectedGrossProfit.partial,
        poasIsPartial: selectedPoas.partial,
        orders,
        ...(hasGoods ? {goods} : {}),
        ...(selectedGrossProfit.allowed ? {contributionBeforeMarketing} : {}),
        pno: mediaQualityBlocked ? null : calculatePnoPercent(total.spendMarketing?.amount, turnover?.amount),
        ...(selectedPoas.allowed ? {poas: selectedPoas.value} : {}),
        roas: mediaQualityBlocked ? null : divideRatio(turnover?.amount, total.spendMarketing?.amount),
        ...(selectedProfit.allowed ? {margin: mediaQualityBlocked ? null : selectedProfit.partial ? partialProfitabilityMarginPercent(partialProfitability) : afterAdsMetricsComplete ? dividePercent(moneyAmount(profit), moneyAmount(turnover)) : null} : {}),
        marginIsPartial: !mediaQualityBlocked && selectedProfit.partial && partialProfitabilityMarginPercent(partialProfitability) !== null,
        ctr: dividePercent(total.clicks, total.impressions),
        clickConversionRate: dividePercent(orders, total.clicks),
        cpc: mediaQualityBlocked ? null : divideMoney(total.spendMarketing, total.clicks),
        cpm: mediaQualityBlocked ? null : divideMoney(total.spendMarketing, total.impressions ? total.impressions / 1000 : 0),
        costPerOrder: mediaQualityBlocked ? null : divideMoney(total.spendMarketing, orders),
        averageOrderValue: divideMoney(turnover, orders),
    };
}

export function LabLoadingOverlay({loading}) {
    if (!loading) {
        return null;
    }

    return <div className="absolute inset-0 z-10 flex items-center justify-center rounded-[inherit] bg-white dark:bg-slate-900">
        <Spinner size="spinner-medium"/>
    </div>;
}

function EmphasizedMetricText({children, quality = false}) {
    if (typeof children !== "string") {
        return children;
    }

    const parts = children.split(/([+−-]?\d+(?:[ \u00a0\u202f]\d{3})*(?:[,.]\d+)?(?:\s*(?:%|×|x|Kč|[A-Z]{3}))?)/g);

    return parts.map((part, index) => index % 2 === 1
        ? <strong key={`${part}-${index}`} className={`whitespace-nowrap font-semibold ${part.trimStart().startsWith("−") || part.trimStart().startsWith("-") ? "text-datixo-negative" : quality ? "text-slate-500 dark:text-slate-400" : "text-slate-700 dark:text-slate-200"}`}>{part}</strong>
        : part
    );
}

export function KpiCard({label, value, valueClassName = "", valueTooltip = null, valueTooltipLabel, meta, metaTooltip = null, metaTooltipLabel, metaTone = "default", helper, info, infoLabel = "More information", tone = "default", metaClassName = "", emphasizeMeta = true, emphasizeHelper = true, loading = false}) {
    const toneClasses = {
        default: "border-slate-200 dark:border-slate-700",
        good: "border-emerald-200 bg-emerald-50/50 dark:border-emerald-900 dark:bg-emerald-950/20",
        warn: "border-amber-200 bg-amber-50/60 dark:border-amber-900 dark:bg-amber-950/20",
        bad: "border-rose-200 bg-rose-50/60 dark:border-rose-900 dark:bg-rose-950/20",
    };

    return <div aria-busy={loading} className={`app-surface relative h-full min-w-0 rounded-lg border bg-white p-4 shadow-sm dark:bg-slate-800 ${toneClasses[tone] ?? toneClasses.default}`}>
        <LabLoadingOverlay loading={loading}/>
        <div className="flex min-w-0 items-start gap-1.5">
            <div className="min-w-0 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
            {info && <ReportingInfoTooltip ariaLabel={infoLabel}>{info}</ReportingInfoTooltip>}
        </div>
        <div className={`mt-3 min-w-0 whitespace-nowrap text-2xl font-bold ${hasNegativeMetricValue(value) ? "text-datixo-negative" : "text-slate-900 dark:text-slate-100"} ${valueClassName}`}>
            {loading
                ? "—"
                : valueTooltip
                ? <Tooltip
                    bg="dark"
                    size="md"
                    position="bottom"
                    className="block min-w-0 max-w-full"
                    triggerClassName="w-full min-w-0 max-w-full"
                    ariaLabel={valueTooltipLabel ?? (typeof value === "string" ? value : "More information")}
                    trigger={<span className="block min-w-0 max-w-full truncate text-left">{value}</span>}
                >
                    <div className="text-xs text-gray-200">{valueTooltip}</div>
                </Tooltip>
                : value}
        </div>
        {!loading && meta && <div className={`mt-2 text-xs ${metaTone === "entity" ? "text-violet-700 dark:text-violet-300" : metaTone === "unavailable" ? "text-slate-500 dark:text-slate-400" : "text-slate-500 dark:text-slate-400"} ${metaClassName}`}>{metaTooltip
            ? <Tooltip bg="dark" size="md" position="bottom" className="block min-w-0" triggerClassName={`block w-full min-w-0 truncate text-left ${metaTone === "entity" ? "text-violet-700 hover:text-violet-800 dark:text-violet-300 dark:hover:text-violet-200" : metaTone === "unavailable" ? "text-slate-500 dark:text-slate-400" : ""}`} ariaLabel={metaTooltipLabel ?? (typeof metaTooltip === "string" ? metaTooltip : "More information")} trigger={emphasizeMeta ? <EmphasizedMetricText quality={metaTone === "unavailable"}>{meta}</EmphasizedMetricText> : meta}>
                <div className="text-xs text-gray-200">{metaTooltip}</div>
            </Tooltip>
            : emphasizeMeta ? <EmphasizedMetricText quality={metaTone === "unavailable"}>{meta}</EmphasizedMetricText> : meta}</div>}
        {!loading && helper && <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{emphasizeHelper ? <EmphasizedMetricText>{helper}</EmphasizedMetricText> : helper}</div>}
    </div>;
}

export function OrderStatusSummary({received, completed, cancelled, renderNumber, t, className = ""}) {
    return <div className={`flex flex-wrap gap-x-3 gap-y-1 ${className}`}>
        {received !== undefined && <span>{t("reporting_lab.orders.received", {defaultValue: "Přijaté"})} <strong className="font-semibold text-slate-700 dark:text-slate-200">{renderNumber(received)}</strong></span>}
        {completed !== undefined && <span>{t("reporting_lab.orders.completed_short", {defaultValue: "Dokonč."})} <strong className="font-semibold text-slate-700 dark:text-slate-200">{renderNumber(completed)}</strong></span>}
        {cancelled !== undefined && <span>{t("reporting_lab.orders.cancelled_short", {defaultValue: "Storno"})} <strong className="font-semibold text-slate-700 dark:text-slate-200">{renderNumber(cancelled)}</strong></span>}
    </div>;
}

export function OrdersKpiCard({label, value, received, completed, cancelled, renderNumber, t, loading = false}) {
    return <div className="app-surface relative h-full rounded-lg border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <LabLoadingOverlay loading={loading}/>
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{label ?? t("reporting_lab.kpi.orders", {defaultValue: "Objednávky"})}</div>
        <div className="mt-3 whitespace-nowrap text-2xl font-bold text-slate-900 dark:text-slate-100">{renderNumber(value)}</div>
        <OrderStatusSummary
            received={received}
            completed={completed}
            cancelled={cancelled}
            renderNumber={renderNumber}
            t={t}
            className="mt-2 text-xs text-slate-500 dark:text-slate-400"
        />
    </div>;
}

export function ReportingPageDescription({title, children, className = "", actions = null}) {
    return <div className={`app-surface rounded-lg border border-violet-200 bg-violet-50 p-4 text-sm text-violet-900 dark:border-violet-900/60 dark:bg-violet-950/30 dark:text-violet-100 ${className}`}>
        <div className="flex flex-col gap-4 2xl:flex-row 2xl:items-center 2xl:justify-between">
            <div className="min-w-0 flex-1">
                <div className="font-semibold">{title}</div>
                <div className="mt-1 text-violet-800 dark:text-violet-200">{children}</div>
            </div>
            {actions && <div className="flex w-full min-w-0 justify-start 2xl:w-auto 2xl:shrink-0 2xl:justify-end">{actions}</div>}
        </div>
    </div>;
}

export function SnapshotCompletenessNotice() {
    return null;
}

export function snapshotCompletenessAlert() {
    return reportingSnapshotCompletenessAlert();
}

function hasNegativeMetricValue(content) {
    if (typeof content === "number") {
        return content < 0;
    }

    if (typeof content === "string") {
        return /^[\s]*[−-]\s*\d/.test(content);
    }

    return Array.isArray(content) && content.some(hasNegativeMetricValue);
}

function styleTableMetricValues(children) {
    return React.Children.map(children, (child) => {
        if (!React.isValidElement(child)) {
            return child;
        }

        const styledChildren = child.props.children ? styleTableMetricValues(child.props.children) : child.props.children;
        const isNegativeCell = child.type === "td"
            && String(child.props.className ?? "").includes("text-right")
            && hasNegativeMetricValue(child.props.children);

        return React.cloneElement(
            child,
            isNegativeCell ? {className: `${child.props.className ?? ""} !text-datixo-negative`} : {},
            styledChildren
        );
    });
}

export function Panel({title, subtitle = null, children, className = "", contentClassName = "", loading = false, actions = null}) {
    const styledChildren = styleTableMetricValues(children);

    return <section className={`app-surface relative rounded-lg border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800 ${className}`}>
        <LabLoadingOverlay loading={loading}/>
        {(title || subtitle || actions) && <div className="flex min-h-12 items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 dark:border-slate-700">
            <div className="min-w-0"><h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">{title}</h2>{subtitle && <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">{subtitle}</p>}</div>
            {actions && <div className="shrink-0">{actions}</div>}
        </div>}
        <div className={`reporting-table-content p-4
            [&_table]:w-full [&_table]:text-sm
            [&_thead]:bg-slate-100/80 dark:[&_thead]:bg-slate-900/60
            [&_thead_tr]:border-b [&_thead_tr]:border-slate-200 dark:[&_thead_tr]:border-slate-700
            [&_th]:!px-3 [&_th]:!py-3 [&_th]:text-xs [&_th]:font-semibold [&_th]:text-slate-600 dark:[&_th]:text-slate-300
            [&_tbody]:divide-y [&_tbody]:divide-slate-200 dark:[&_tbody]:divide-slate-700
            [&_tbody_tr]:transition-colors
            [&_tbody_tr:nth-child(even)]:bg-slate-50 dark:[&_tbody_tr:nth-child(even)]:bg-slate-900/30
            [&_tbody_tr:nth-child(odd):hover]:bg-violet-50 dark:[&_tbody_tr:nth-child(odd):hover]:bg-violet-950/30
            [&_tbody_tr:nth-child(even):hover]:bg-violet-100 dark:[&_tbody_tr:nth-child(even):hover]:bg-violet-950/50
            [&_td]:!px-3 [&_td]:!py-3.5 [&_td]:text-slate-600 dark:[&_td]:text-slate-300
            [&_td.text-right]:whitespace-nowrap [&_th.text-right]:whitespace-nowrap
            [&_tbody_td:first-child]:font-semibold [&_tbody_td:first-child]:text-slate-900 dark:[&_tbody_td:first-child]:text-slate-100 ${contentClassName}`}>{styledChildren}</div>
    </section>;
}

export function TablePagination({page, pageSize, totalItems, onPageChange, onPageSizeChange = null, pageSizeOptions = [10, 25, 50, 100], pageSizeSelectClassName = null, t}) {
    const pageCount = Math.max(1, Math.ceil(totalItems / pageSize));
    const currentPage = Math.min(Math.max(page, 1), pageCount);
    const firstItem = totalItems === 0 ? 0 : ((currentPage - 1) * pageSize) + 1;
    const lastItem = Math.min(currentPage * pageSize, totalItems);
    const buttonClass = "inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 transition hover:border-violet-300 hover:text-violet-700 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300";

    return <div className="mt-4 flex flex-col gap-3 border-t border-slate-100 pt-4 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
            {onPageSizeChange && <select
                value={pageSize}
                onChange={(event) => onPageSizeChange(Number(event.target.value))}
                className={pageSizeSelectClassName ?? "min-w-16 rounded-md border border-slate-200 bg-white py-1.5 pl-3 pr-8 text-sm font-semibold text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"}
                aria-label={t("reporting_lab.table.page_size", {defaultValue: "Počet záznamů na stránku"})}
            >
                {pageSizeOptions.map((option) => <option key={option} value={option}>{formatChartNumber(option)}</option>)}
            </select>}
            <span>
            {t("reporting_lab.table.visible_range", {
                defaultValue: "{{from}}–{{to}} z {{total}} záznamů",
                from: formatChartNumber(firstItem),
                to: formatChartNumber(lastItem),
                total: formatChartNumber(totalItems),
            })}
            </span>
        </div>
        <div className="flex items-center gap-3">
            <span>{t("reporting_lab.table.page_count", {defaultValue: "Strana {{page}} z {{pages}}", page: formatChartNumber(currentPage), pages: formatChartNumber(pageCount)})}</span>
            <div className="flex items-center gap-1">
                <button
                    type="button"
                    className={buttonClass}
                    disabled={currentPage <= 1}
                    onClick={() => onPageChange(currentPage - 1)}
                    title={t("reporting_lab.table.previous_page", {defaultValue: "Předchozí strana"})}
                >
                    <FontAwesomeIcon icon={faChevronLeft}/>
                </button>
                <button
                    type="button"
                    className={buttonClass}
                    disabled={currentPage >= pageCount}
                    onClick={() => onPageChange(currentPage + 1)}
                    title={t("reporting_lab.table.next_page", {defaultValue: "Další strana"})}
                >
                    <FontAwesomeIcon icon={faChevronRight}/>
                </button>
            </div>
        </div>
    </div>;
}

export function filterRowsBySearch(rows, query, getSearchValues) {
    const normalizedQuery = query.trim().toLocaleLowerCase("cs-CZ");

    if (!normalizedQuery) {
        return rows;
    }

    return rows.filter((row) => {
        const values = getSearchValues(row);
        return (Array.isArray(values) ? values : [values])
            .some((value) => String(value ?? "").toLocaleLowerCase("cs-CZ").includes(normalizedQuery));
    });
}

export function TableSearch({value, onChange, visible, t}) {
    if (!visible) {
        return null;
    }

    const label = t("reporting_lab.table.search", {defaultValue: "Hledání..."});
    const clearLabel = t("clear", {defaultValue: "Vymazat"});

    return <ModernSearchInput value={value} onChange={onChange} label={label} clearLabel={clearLabel} className="mb-4"/>;
}

function normalizedSortValue(value) {
    if (value == null) {
        return null;
    }

    if (typeof value === "object" && "amount" in value) {
        return Number(value.amount);
    }

    if (typeof value === "number") {
        return value;
    }

    return String(value).toLocaleLowerCase();
}

export function useSortableRows(rows, initialKey, getValue, initialDirection = "desc") {
    const [sort, setSort] = useState({key: initialKey, direction: initialDirection});
    const sortedRows = useMemo(() => [...rows].sort((left, right) => {
        const leftValue = normalizedSortValue(getValue(left, sort.key));
        const rightValue = normalizedSortValue(getValue(right, sort.key));

        if (leftValue == null && rightValue == null) return 0;
        if (leftValue == null) return 1;
        if (rightValue == null) return -1;

        const result = typeof leftValue === "number"
            ? leftValue - rightValue
            : leftValue.localeCompare(rightValue, "cs", {numeric: true, sensitivity: "base"});

        return sort.direction === "asc" ? result : -result;
    }), [rows, sort, getValue]);

    const toggleSort = (key) => setSort((current) => ({
        key,
        direction: current.key === key && current.direction === "desc" ? "asc" : "desc",
    }));
    const resetSort = () => setSort({key: initialKey, direction: initialDirection});

    return {sortedRows, sort, toggleSort, resetSort};
}

export function SortableHeader({label, tooltip = null, sortKey, sort, onSort, align = "left", className = ""}) {
    const {t} = useTranslation();
    const active = sort.key === sortKey;
    const icon = !active ? faSort : sort.direction === "asc" ? faChevronUp : faChevronDown;

    return <th aria-sort={active ? (sort.direction === "asc" ? "ascending" : "descending") : "none"} className={`${align === "right" ? "text-right" : "text-left"} ${className}`}>
        <div className={`flex items-center gap-1.5 whitespace-nowrap ${align === "right" ? "justify-end" : "justify-start"}`}>
            <button
                type="button"
                onClick={() => onSort(sortKey)}
                className="inline-flex min-w-0 items-center gap-1.5 whitespace-nowrap"
            >
                <span>{label}</span>
                <FontAwesomeIcon icon={icon} className={active ? "text-violet-600" : "text-slate-400"}/>
            </button>
            {tooltip && <ReportingInfoTooltip ariaLabel={t("reporting_lab.kpi.info_label", {defaultValue: "Vysvětlení metriky"})}>{tooltip}</ReportingInfoTooltip>}
        </div>
    </th>;
}

export function MetricHelpLabel({label, tooltip}) {
    const {t} = useTranslation();
    return <span className="inline-flex items-center justify-end gap-1.5 whitespace-nowrap">
        <span>{label}</span>
        <ReportingInfoTooltip ariaLabel={t("reporting_lab.kpi.info_label", {defaultValue: "Vysvětlení metriky"})}>{tooltip}</ReportingInfoTooltip>
    </span>;
}

export function ExpandablePanelTitle({title, expanded, onToggle, expandLabel, collapseLabel}) {
    const actionLabel = expanded ? collapseLabel : expandLabel;

    return <span className="flex items-center gap-2">
        <button
            type="button"
            onClick={onToggle}
            className="group relative inline-flex h-7 w-8 shrink-0 items-center justify-center gap-0.5 rounded-md border border-slate-200 bg-white text-slate-500 hover:border-violet-300 hover:text-violet-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
            aria-label={actionLabel}
        >
            {expanded
                ? <><FontAwesomeIcon icon={faChevronRight}/><FontAwesomeIcon icon={faChevronLeft}/></>
                : <><FontAwesomeIcon icon={faChevronLeft}/><FontAwesomeIcon icon={faChevronRight}/></>}
            <span className="pointer-events-none absolute left-1/2 top-full z-50 mt-2 -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-800 px-2 py-1 text-xs font-normal text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                {actionLabel}
            </span>
        </button>
        <span>{title}</span>
    </span>;
}

export function MetricBasisToggle({value, onChange, metricBasisMeta}) {
    return <div className="grid w-full min-w-0 grid-cols-1 gap-2 sm:grid-cols-2 2xl:w-auto">
        {Object.values(MetricBasis).map((basis) => {
            const selected = value === basis;
            const meta = metricBasisMeta[basis];

            return <button
                key={basis}
                type="button"
                onClick={() => onChange(basis)}
                className={`min-w-0 rounded-lg border px-4 py-2.5 text-left transition 2xl:w-[360px] ${selected ? "border-violet-500 bg-white shadow-sm ring-2 ring-violet-200 dark:bg-slate-800" : "border-violet-200 bg-white/70 hover:border-violet-300 dark:border-violet-900 dark:bg-slate-800/60"}`}
            >
                <div className={`text-sm font-bold ${selected ? "text-violet-700 dark:text-violet-200" : "text-slate-800 dark:text-slate-100"}`}>{meta.label}</div>
                <div className="mt-0 text-xs leading-5 text-slate-500 dark:text-slate-400">{meta.description}</div>
            </button>;
        })}
    </div>;
}

export function compactChannelName(channel) {
    return channel?.name ?? channel?.id ?? "-";
}

export function channelRowName(row, rows = [], t = null) {
    const displayName = (channel) => channel?.id === "Unknown" && channel?.name === "Unknown" && t
        ? t("reporting_lab.marketing.unknown_channel", {defaultValue: "Neznámý zdroj"})
        : compactChannelName(channel);
    const baseName = displayName(row.channel);
    const duplicates = rows.filter((candidate) => displayName(candidate.channel) === baseName).length;

    if (duplicates <= 1) {
        return baseName;
    }

    const detail = row.accountNames?.join(", ") || String(row.channel?.id ?? "").slice(0, 8);
    return detail ? `${baseName} · ${detail}` : baseName;
}

function channelTableValue(row, key) {
    return {
        channel: compactChannelName(row.channel),
        marketing: channelSpendPresentation(row).value,
        revenue: row.turnover,
        profit: row.profit,
        pno: row.pno,
        orders: row.orders,
    }[key];
}

export function overviewMetricAvailability({snapshotIncomplete = false, summaryUnavailable = snapshotIncomplete, selectedCentreFilter = false, attributionStatus = null, mediaSourceQuality = null, hasProfit = false, hasProfitField = false}) {
    const selectedCentreAttributionIncomplete = selectedCentreFilter && attributionStatus !== null;
    const afterAdMetricsAvailable = !summaryUnavailable && !selectedCentreAttributionIncomplete && !mediaSourceQualityBlocksMetrics(mediaSourceQuality);
    const selectedCentreBreakdownAvailable = !selectedCentreAttributionIncomplete;

    return {
        selectedCentreAttributionIncomplete,
        marketingSpend: !summaryUnavailable,
        pno: afterAdMetricsAvailable,
        profit: afterAdMetricsAvailable && hasProfit,
        profitCard: hasProfitField,
        profitBreakdown: selectedCentreBreakdownAvailable && hasProfitField,
        channelSubtotal: selectedCentreAttributionIncomplete,
        channelReview: selectedCentreBreakdownAvailable,
    };
}

export function overviewHasOnlyMissingPurchasePrices({partialProfitability, mediaSourceQuality}) {
    const knownLineCount = partialProfitability?.knownLineCount;
    const missingLineCount = partialProfitability?.actionableUnknownLineCount;
    return !mediaSourceQualityBlocksMetrics(mediaSourceQuality)
        && partialProfitability?.available === false
        && typeof knownLineCount === "number" && Number.isInteger(knownLineCount) && knownLineCount === 0
        && typeof missingLineCount === "number" && Number.isInteger(missingLineCount) && missingLineCount > 0;
}

export function rankRiskyChannels(rows) {
    return [...(rows ?? [])]
        .filter((row) => moneyAmount(row.spendMarketing) > 0 && row.pno != null)
        .sort((a, b) => b.pno - a.pno)
        .slice(0, 5);
}

export function overviewOrderKpiValues({orders, received, completed, cancelled, snapshot}) {
    const available = snapshotHasPublishedDays(snapshot);
    return {
        value: available ? orders : null,
        received: available && received != null ? received : undefined,
        completed: available && completed != null ? completed : undefined,
        cancelled: available && cancelled != null ? cancelled : undefined,
    };
}

export default function ReportingLab({project}) {
    const {t, i18n} = useTranslation();
    const {since, until, centresAllowListed, domainsAllowListed} = useContext(FilterContext);
    const {getAuthorityConfiguration} = useContext(GlobalContext);
    const renderCurrencyValue = useRenderCurrencyValue();
    const renderNumber = useRenderNumber();
    const renderPercentValue = useRenderPercentValue();

    const [shopPerformance, setShopPerformance] = useState(null);
    const [channelData, setChannelData] = useState([]);
    const [snapshot, setSnapshot] = useState(null);
    const [loading, setLoading] = useState(true);
    const {et, retentionExpired} = useReportingError(project.id, setLoading);
    const [channelLimit, setChannelLimit] = useState(10);
    const [channelPage, setChannelPage] = useState(1);
    const [channelSearch, setChannelSearch] = useState("");
    const [metricBasis, setMetricBasis] = useState(MetricBasis.Marketing);
    const metricBasisMeta = useMemo(() => ({
        [MetricBasis.Marketing]: {
            label: t("reporting_lab.metric_basis.marketing.label", {defaultValue: "Marketingový výkon"}),
            shortLabel: t("reporting_lab.metric_basis.marketing.short_label", {defaultValue: "Bez storen"}),
            description: t("reporting_lab.metric_basis.marketing.description", {defaultValue: "Objednávky, které přišly a nejsou stornované. Vhodné pro vyhodnocení marketingu."}),
        },
        [MetricBasis.Completed]: {
            label: t("reporting_lab.metric_basis.business.label", {defaultValue: "Obchodní výkon"}),
            shortLabel: t("reporting_lab.metric_basis.business.short_label", {defaultValue: "Dokončené"}),
            description: t("reporting_lab.metric_basis.business.description", {defaultValue: "Jen dokončené objednávky. Vhodné pro majitele, finance a reálný vyfakturovaný výkon."}),
        },
    }), [t]);

    const sinceString = since.format("YYYY-MM-DD");
    const untilString = until.format("YYYY-MM-DD");
    const coverageRange = effectiveReportingRange(snapshot, sinceString, untilString);
    const centreFilterKey = (centresAllowListed ?? []).join("|");
    const domainFilterKey = (domainsAllowListed ?? []).join("|");
    const selectedCentreFilter = (centresAllowListed ?? []).length > 0;

    const erpFilter = useMemo(() => ({
        since: sinceString,
        until: untilString,
        selectedCentreIds: centresAllowListed,
        selectedDomainIds: domainsAllowListed,
    }), [sinceString, untilString, centreFilterKey, domainFilterKey]);

    useEffect(() => {
        if (!project?.id) {
            return;
        }

        setLoading(true);
        setShopPerformance(null);
        setChannelData([]);
        setSnapshot(null);

        return startCancelableReportingRequest(
            (signal) => new ErpCalculationApi(getAuthorityConfiguration()).reportingOverview({projectId: project.id, erpFilter}, {signal}),
            {
                onSuccess: (result) => {
                    setShopPerformance(result.shopPerformance);
                    setChannelData((result.channels?.items ?? []).map((item) => ({
                        channel: {id: item.id, name: item.name},
                        overview: item.overview,
                    })));
                    setSnapshot(result.snapshot);
                    setLoading(false);
                },
                onError: (error) => {
                    et(error).then((message) => { if (message) toast.error(message); });
                },
            },
        );
    }, [project?.id, erpFilter]);

    const channelRows = useMemo(() => {
        return channelData.map(({channel, overview}) => buildChannelRow(channel, overview, metricBasis));
    }, [channelData, metricBasis]);

    const selectedShopData = useMemo(() => {
        if (!shopPerformance) {
            return {};
        }

        return {
            turnover: metricBasis === MetricBasis.Completed ? shopPerformance.orderTurnoverNet : shopPerformance.orderTurnoverGross,
            profit: metricBasis === MetricBasis.Completed ? shopPerformance.orderProfitNet : shopPerformance.orderProfitGross,
            average: metricBasis === MetricBasis.Completed ? shopPerformance.orderAverageNet : shopPerformance.orderAverageGross,
            orders: metricBasis === MetricBasis.Completed ? shopPerformance.orderAmountNet : shopPerformance.orderAmountGross,
            ordersCompleted: shopPerformance.orderAmountCompleted ?? 0,
            ordersReceived: shopPerformance.orderAmountReceived ?? 0,
            ordersCancelled: shopPerformance.orderAmountCancelled ?? 0,
            turnoverGranular: metricBasis === MetricBasis.Completed ? shopPerformance.orderTurnoverNetGranular : shopPerformance.orderTurnoverGrossGranular,
            profitGranular: metricBasis === MetricBasis.Completed ? shopPerformance.orderProfitNetGranular : shopPerformance.orderProfitGrossGranular,
            ordersGranular: metricBasis === MetricBasis.Completed ? shopPerformance.orderAmountNetGranular : shopPerformance.orderAmountGrossGranular,
            ordersCompletedGranular: shopPerformance.orderAmountCompletedGranular ?? {},
            ordersReceivedGranular: shopPerformance.orderAmountReceivedGranular ?? {},
            ordersCancelledGranular: shopPerformance.orderAmountCancelledGranular ?? {},
            spendMarketing: shopPerformance.orderSpendMarketing,
        };
    }, [shopPerformance, metricBasis]);
    const profitKey = metricBasis === MetricBasis.Completed ? "orderProfitNet" : "orderProfitGross";
    const shopCostQuality = purchaseCostQuality(shopPerformance ? [shopPerformance] : [], metricBasis);
    const hasProfitField = hasReturnedMoneyMetric(shopPerformance ? [shopPerformance] : [], profitKey);
    const metricBasisData = overviewMetricBasisData(shopPerformance, metricBasis);
    const partialProfitability = metricBasisData.partialProfitability;
    const mediaQualityBlocked = mediaSourceQualityBlocksMetrics(shopPerformance?.mediaSourceQuality);
    const profitPresentation = selectProfitabilityMetric({
        fullValue: mediaQualityBlocked ? undefined : shopPerformance?.[profitKey],
        fullComplete: !mediaQualityBlocked && shopCostQuality.complete,
        partialProfitability: mediaQualityBlocked ? null : partialProfitability,
        partialField: "knownProfitAfterAllMarketing",
    });
    const hasProfit = profitPresentation.value != null;
    const snapshotIncomplete = isSnapshotPeriodIncomplete(snapshot);
    const summaryUnavailable = !snapshotHasPublishedDays(snapshot);
    const attributionStatus = loading
        ? null
        : selectedCentreSpendAttributionStatus(shopPerformance?.centreSpendAttribution, selectedCentreFilter);
    const metricAvailability = overviewMetricAvailability({
        snapshotIncomplete,
        summaryUnavailable,
        selectedCentreFilter,
        attributionStatus,
        mediaSourceQuality: shopPerformance?.mediaSourceQuality,
        hasProfit,
        hasProfitField,
    });

    const sortedChannels = useMemo(() => {
        if (!snapshotHasPublishedDays(snapshot)) {
            return [];
        }

        return [...channelRows]
            .filter((row) => moneyAmount(row.spendMarketing) > 0 || moneyAmount(row.turnover) > 0 || row.orders > 0)
            .sort((a, b) => moneyAmount(b.turnover) - moneyAmount(a.turnover));
    }, [channelRows, snapshot]);
    const filteredTableChannels = filterRowsBySearch(sortedChannels, channelSearch, (row) => [channelRowName(row, sortedChannels, t), row.accountNames]);
    const {sortedRows: sortedTableChannels, sort: channelTableSort, toggleSort: toggleChannelTableSort} = useSortableRows(
        filteredTableChannels,
        "revenue",
        (row, key) => key === "pno" && metricAvailability.selectedCentreAttributionIncomplete ? null : channelTableValue(row, key),
    );
    const channelPageCount = Math.max(1, Math.ceil(sortedTableChannels.length / channelLimit));
    const safeChannelPage = Math.min(channelPage, channelPageCount);
    const visibleChannelRows = sortedTableChannels.slice((safeChannelPage - 1) * channelLimit, safeChannelPage * channelLimit);

    useEffect(() => {
        setChannelPage(1);
    }, [channelLimit, erpFilter]);

    const topChannels = sortedChannels.slice(0, 8);
    const channelMixRows = useMemo(() => {
        const rowsByName = new Map();

        sortedChannels.forEach((row) => {
            const name = compactChannelName(row.channel);
            const existing = rowsByName.get(name) ?? {
                name,
                isUnknownSource: row.channel?.id === "Unknown" && row.channel?.name === "Unknown",
                turnover: createEmptyMoney(row.turnover?.currency),
            };

            rowsByName.set(name, {
                ...existing,
                isUnknownSource: existing.isUnknownSource && row.channel?.id === "Unknown" && row.channel?.name === "Unknown",
                turnover: addMoney(existing.turnover, row.turnover),
            });
        });

        return [...rowsByName.values()]
            .map((row) => ({
                ...row,
                displayName: row.isUnknownSource ? channelRowName({channel: {id: "Unknown", name: "Unknown"}}, [], t) : row.name,
            }))
            .filter((row) => moneyAmount(row.turnover) > 0)
            .sort((a, b) => moneyAmount(b.turnover) - moneyAmount(a.turnover))
            .slice(0, 8);
    }, [sortedChannels, t]);
    const riskyChannels = rankRiskyChannels(sortedChannels);

    const displayedTurnover = snapshotPeriodValue(selectedShopData.turnover, snapshot);
    const displayedProfit = snapshotPeriodValue(profitPresentation.value, snapshot);
    const displayedSpend = snapshotPeriodValue(selectedShopData.spendMarketing, snapshot);
    const orderKpiValues = overviewOrderKpiValues({
        orders: selectedShopData.orders,
        received: selectedShopData.ordersReceived,
        completed: selectedShopData.ordersCompleted,
        cancelled: selectedShopData.ordersCancelled,
        snapshot,
    });
    const margin = metricAvailability.profit ? profitPresentation.partial ? partialProfitabilityMarginPercent(partialProfitability) : dividePercent(moneyAmount(displayedProfit), moneyAmount(displayedTurnover)) : null;
    const pno = metricAvailability.pno ? calculatePnoPercent(displayedSpend?.amount, displayedTurnover?.amount) : null;
    const showProfitTrend = metricAvailability.profitBreakdown;
    const currency = selectedShopData.turnover?.currency ?? projectCurrency(project);
    const activeMetricBasis = metricBasisMeta[metricBasis];

    const dates = withSnapshotMissingDays(snapshot, Object.keys(selectedShopData.turnoverGranular ?? {}));
    const chartTextStyle = {fontSize: 12, color: "#64748b"};
    const chartLegendStyle = {
        textStyle: {fontSize: 14, color: "#64748b"},
        itemWidth: 16,
        itemHeight: 10,
    };
    const revenueTrendOption = {
        color: RevenueTrendColors,
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 38, bottom: 52, containLabel: true},
        tooltip: {
            trigger: "axis",
            formatter: (params) => formatAxisTooltip(params, [
                (value) => formatChartCurrency(value, currency),
                (value) => formatChartCurrency(value, currency),
                formatChartNumber,
            ]),
        },
        legend: {bottom: 4, ...chartLegendStyle},
        xAxis: {type: "category", data: dates.map(formatDateLabel)},
        yAxis: [
            {
                type: "value",
                name: currencyUnit(currency),
                axisLabel: {
                    fontSize: 12,
                    formatter: formatChartNumber,
                },
            },
            {
                type: "value",
                name: t("reporting_lab.chart.axis.orders_short", {defaultValue: "Obj."}),
                minInterval: 1,
                axisLabel: {
                    fontSize: 12,
                    formatter: formatChartNumber,
                },
            },
        ],
        series: [
            {
                name: t("reporting_lab.series.revenue", {defaultValue: "Obrat"}),
                type: "bar",
                data: dates.map((date) => snapshotDateValue(nullableMoneyMajorUnitAmount(selectedShopData.turnoverGranular?.[date]), snapshot, date)),
                barMaxWidth: 34,
            },
            {
                name: t("reporting_lab.series.profit", {defaultValue: "Hrubý zisk po reklamě"}),
                type: "line",
                smooth: true,
                data: dates.map((date) => {
                    const dayMediaSourceQuality = shopPerformance?.mediaSourceQualityGranular?.[date] ?? shopPerformance?.mediaSourceQuality;
                    if (mediaSourceQualityBlocksMetrics(dayMediaSourceQuality)) return null;
                    const partialDay = metricBasisData.partialProfitabilityGranular?.[date];
                    const fullComplete = metricBasisData.purchaseCostUnknownGranular?.[date] === 0 && shopPerformance?.purchaseCostQualityAvailableGranular?.[date] === true;
                    const selected = selectProfitabilityMetric({fullValue: selectedShopData.profitGranular?.[date], fullComplete, partialProfitability: partialDay, partialField: "knownProfitAfterAllMarketing"});
                    return partialProfitabilityChartPoint(snapshotDateValue(nullableMoneyMajorUnitAmount(selected.value), snapshot, date), partialDay, selected.partial, t);
                }),
            },
            {
                name: t("reporting_lab.series.orders", {defaultValue: "Objednávky"}),
                type: "line",
                smooth: true,
                yAxisIndex: 1,
                data: dates.map((date) => snapshotDateValue(selectedShopData.ordersGranular?.[date] ?? 0, snapshot, date)),
            },
        ].filter((series) => showProfitTrend || series.name !== t("reporting_lab.series.profit", {defaultValue: "Hrubý zisk po reklamě"})),
    };

    const channelMixOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        tooltip: {
            trigger: "item",
            formatter: (params) => formatCurrencyItemTooltip(params, currency),
        },
        legend: {bottom: 0, ...chartLegendStyle},
        series: [
            {
                type: "pie",
                radius: ["44%", "68%"],
                center: ["50%", "50%"],
                avoidLabelOverlap: true,
                label: {
                    show: true,
                    formatter: (params) => `${params.name}: ${formatChartPercent(params.percent)}`,
                    ...chartTextStyle,
                },
                labelLine: {
                    show: true,
                    length: 12,
                    length2: 8,
                },
                emphasis: {
                    label: {
                        show: true,
                        formatter: (params) => `${params.name}: ${formatChartPercent(params.percent)}`,
                        fontWeight: 600,
                    },
                },
                data: channelMixRows.map((row) => ({
                    name: row.displayName,
                    value: moneyToMajor(row.turnover),
                })),
            },
        ],
    };

    const spendVsProfitOption = {
        color: ["#7c3aed", "#16a34a"],
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 24, bottom: 54, containLabel: true},
        tooltip: {
            trigger: "axis",
            formatter: (params) => formatAxisTooltip(params, (value) => formatChartCurrency(value, currency)),
        },
        legend: {bottom: 0, ...chartLegendStyle},
        xAxis: {type: "category", data: topChannels.map((row) => channelRowName(row, sortedChannels, t))},
        yAxis: {
            type: "value",
            axisLabel: {
                fontSize: 12,
                formatter: formatChartNumber,
            },
        },
        series: [
            {
                name: t("reporting_lab.series.marketing", {defaultValue: "Marketing"}),
                type: "bar",
                data: topChannels.map(channelSpendChartPoint),
                barMaxWidth: 28,
            },
            {
                name: t("reporting_lab.series.profit", {defaultValue: "Hrubý zisk po reklamě"}),
                type: "bar",
                data: topChannels.map((row) => partialProfitabilityChartPoint(nullableMoneyMajorUnitAmount(row.profit), row.partialProfitability, row.profitIsPartial, t)),
                barMaxWidth: 28,
            },
        ].filter((series) => showProfitTrend || series.name !== t("reporting_lab.series.profit", {defaultValue: "Hrubý zisk po reklamě"})),
    };

    return <WideSecureLayout periodReport retentionExpired={retentionExpired}
        title={t("reporting_lab.pages.overview", {defaultValue: "Celkové shrnutí"})}
        actions={<OrderDashboardFilters hideFilters={false} showMarketingFilters={false} showModeSwitch={false}/>}
        alerts={[snapshotCompletenessAlert(snapshot, t)]}
     intro={<ReportingPageDescription
                    title={t("reporting_lab.descriptions.overview.title", {defaultValue: "K čemu slouží celkové shrnutí"})}
                    actions={<MetricBasisToggle value={metricBasis} onChange={setMetricBasis} metricBasisMeta={metricBasisMeta}/>}
                >
                    {metricBasis === MetricBasis.Marketing
                        ? t("reporting_lab.descriptions.overview.marketing_text", {defaultValue: "Souhrn obratu a zisku z nestornovaných objednávek. Marketingové náklady se počítají za stejné dostupné dny a zvolené filtry."})
                        : t("reporting_lab.descriptions.overview.text", {defaultValue: "Manažerský přehled obratu a zisku z dokončených objednávek. Marketingové náklady se počítají za stejné dostupné dny a zvolené filtry."})}
                </ReportingPageDescription>}>
        <div className="space-y-4">

            <ReportingSnapshotStatus snapshots={snapshot} mediaSourceQuality={snapshotStatusMediaSourceQuality(snapshot, shopPerformance?.mediaSourceQuality)}/>
            {selectedCentreFilter && <CentreSpendAttributionNotice attribution={shopPerformance?.centreSpendAttribution} selectedCentreFilter loading={loading}/>}
            <ReportingCostCoverageContext data={partialProfitability} reportContext={metricBasisData.reportContext} loading={loading} requestEnabled={!loading && coverageRange !== null} requestFilters={coverageRange ? {...coverageRange, selectedCentreIds: centresAllowListed, selectedDomainIds: domainsAllowListed} : undefined}/>

            <div className="grid grid-cols-12 gap-4">
                    <div className="col-span-12 md:col-span-6 xl:col-span-3">
                        <KpiCard loading={loading} label={t("reporting_lab.kpi.revenue", {defaultValue: "Obrat"})} value={summaryUnavailable ? "—" : renderCurrencyValue(displayedTurnover, true)} meta={summaryUnavailable ? snapshotUnavailableShort(snapshot, t) : metricBasis === MetricBasis.Marketing ? t("reporting_lab.kpi.revenue_marketing_meta", {defaultValue: "Přijaté bez storen"}) : t("reporting_lab.kpi.revenue_completed_meta", {defaultValue: "Dokončené"})}/>
                    </div>
                    {metricAvailability.profitCard && <div className="col-span-12 md:col-span-6 xl:col-span-3">
                        <KpiCard loading={loading} label={profitPresentation.partial ? t("reporting_lab.kpi.partial_profit", {defaultValue: "Dílčí hrubý zisk po reklamě"}) : t("reporting_lab.kpi.profit", {defaultValue: "Hrubý zisk po reklamě"})} value={metricAvailability.profit ? renderCurrencyValue(displayedProfit, true) : "—"} valueClassName="" meta={summaryUnavailable ? snapshotUnavailableShort(snapshot, t) : profitPresentation.partial ? `${t("reporting_lab.kpi.partial_margin_inline", {defaultValue: "Dílčí marže po reklamě"})} ${renderPercentValue(margin)}` : hasProfit ? `${t("reporting_lab.kpi.margin_inline", {defaultValue: "Marže po reklamě"})} ${renderPercentValue(margin)}` : metricAvailability.selectedCentreAttributionIncomplete ? t("reporting_lab.centre_spend_attribution.selected_short", {defaultValue: "Souhrn vybraných středisek je neúplný"}) : !shopCostQuality.complete && overviewHasOnlyMissingPurchasePrices({partialProfitability, mediaSourceQuality: shopPerformance?.mediaSourceQuality}) ? t("reporting_lab.cost_quality.missing_purchase_prices_short", {defaultValue: "Chybí nákupní ceny"}) : !shopCostQuality.complete ? t("reporting_lab.cost_quality.incomplete_short", {defaultValue: "Neúplné náklady"}) : t("reporting_lab.cost_quality.profit_unavailable", {defaultValue: "Není dostupná"})} metaTone={metricAvailability.profit && !summaryUnavailable ? "default" : "unavailable"} info={summaryUnavailable ? t("reporting_lab.snapshot.incomplete_help", {defaultValue: "Souhrny a srovnání za celé období nejsou dostupné. Grafy a dílčí rozklady zahrnují pouze publikované dny."}) : metricAvailability.selectedCentreAttributionIncomplete ? t("reporting_lab.centre_spend_attribution.selected", {defaultValue: "Výkon vybraných středisek ukazuje známý přiřazený mezisoučet. Nepřiřazené náklady nelze přiřadit k vybranému středisku, proto jsou jeho souhrnné PNO a zisk neúplné."}) : profitPresentation.partial ? <>{t("reporting_lab.partial_profitability.help", {defaultValue: "Výsledek vychází z řádků se známou tržbou a nákupním nákladem. Od jejich hrubého zisku se odečítají všechny reklamní náklady stejného výběru."})}{partialProfitability?.quality === "estimated" && <p>{t("reporting_lab.partial_profitability.estimate_help", {defaultValue: "Výsledek zahrnuje odhadované nákupní ceny, nikoli výhradně doložené historické ocenění."})}</p>}</> : !hasProfit && !shopCostQuality.complete ? t("reporting_lab.cost_quality.incomplete", {defaultValue: "Část nákladových podkladů chybí nebo není ověřena. Marži a zisk za celé období proto nelze spolehlivě vyčíslit."}) : null} infoLabel={t("reporting_lab.kpi.info_label", {defaultValue: "Vysvětlení metriky"})}/>
                    </div>}
                    <div className="col-span-12 md:col-span-6 xl:col-span-3">
                        <KpiCard
                            loading={loading}
                            label={t("reporting_lab.kpi.marketing", {defaultValue: "Marketing"})}
                            value={<span className="flex flex-wrap items-center gap-2">
                                <span>{metricAvailability.marketingSpend ? renderCurrencyValue(displayedSpend, true) : "—"}</span>
                                <span className="rounded-md bg-violet-100 px-2 py-1 text-xs font-semibold text-violet-700 dark:bg-violet-950/60 dark:text-violet-200">
                                    PNO {metricAvailability.pno ? renderPercentValue(pno) : "—"}
                                </span>
                            </span>}
                            helper={t("reporting_lab.metrics_help.marketing_pno_short", {defaultValue: "PNO: marketingové náklady jako podíl obratu."})}
                            info={<>{metricBasis === MetricBasis.Marketing
                                ? t("reporting_lab.metrics_help.marketing_pno", {defaultValue: "PNO = veškeré marketingové náklady za zvolené období a filtry / obrat nestornovaných objednávek × 100 %. Náklady se nevylučují podle stavu objednávek."})
                                : t("reporting_lab.metrics_help.overview_marketing_pno", {defaultValue: "Marketingové náklady se počítají za stejné dostupné dny a zvolené filtry. PNO = veškeré marketingové náklady / obrat dokončených objednávek × 100 %. Náklady se nevylučují podle stavu objednávek."})}{metricAvailability.selectedCentreAttributionIncomplete && <p>{t("reporting_lab.centre_spend_attribution.selected", {defaultValue: "Výkon vybraných středisek ukazuje známý přiřazený mezisoučet. Nepřiřazené náklady nelze přiřadit k vybranému středisku, proto jsou jeho souhrnné PNO a zisk neúplné."})}</p>}</>}
                            infoLabel={t("reporting_lab.kpi.info_label", {defaultValue: "Vysvětlení metriky"})}
                        />
                    </div>
                    <div className="col-span-12 md:col-span-6 xl:col-span-3">
                        <OrdersKpiCard loading={loading}
                            label={metricBasis === MetricBasis.Marketing ? t("reporting_lab.kpi.orders", {defaultValue: "Objednávky"}) : t("reporting_lab.products.completed_orders_basis", {defaultValue: "Dokončené objednávky"})}
                            value={orderKpiValues.value}
                            received={orderKpiValues.received}
                            completed={orderKpiValues.completed}
                            cancelled={orderKpiValues.cancelled}
                            renderNumber={renderNumber}
                            t={t}
                        />
                    </div>

                    <Panel loading={loading} title={showProfitTrend ? t("reporting_lab.panels.trend", {defaultValue: "Vývoj obratu, hrubého zisku po reklamě a objednávek"}) : t("reporting_lab.panels.trend_without_profit", {defaultValue: "Vývoj obratu a objednávek"})} className="col-span-12 xl:col-span-8">
                        <EChart option={revenueTrendOption} className="h-80"/>
                    </Panel>
                    <Panel loading={loading} title={t("reporting_lab.panels.channel_share", {defaultValue: "Rozpad top 8 kanálů"})} className="col-span-12 xl:col-span-4">
                        <EChart option={channelMixOption} className="h-72"/>
                    </Panel>
                    {showProfitTrend && <Panel loading={loading} title={t("reporting_lab.panels.spend_vs_profit", {defaultValue: "Hrubý zisk po reklamě a marketing podle kanálu"})} className="col-span-12">
                        <EChart option={spendVsProfitOption} className="h-80"/>
                    </Panel>}

                    <Panel loading={loading} title={t("reporting_lab.panels.channel_performance", {defaultValue: "Kanály podle výkonu"})} className="col-span-12 xl:col-span-8">
                        <TableSearch value={channelSearch} onChange={(value) => { setChannelSearch(value); setChannelPage(1); }} visible={sortedChannels.length > 15} t={t}/>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-100 text-sm dark:divide-slate-700">
                                <thead>
                                <tr className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                                    <SortableHeader label={t("reporting_lab.table.channel", {defaultValue: "Kanál"})} sortKey="channel" sort={channelTableSort} onSort={toggleChannelTableSort}/>
                                    <SortableHeader label={t("reporting_lab.table.marketing", {defaultValue: "Marketing"})} tooltip={metricAvailability.channelSubtotal ? t("reporting_lab.centre_spend_attribution.selected", {defaultValue: "Výkon vybraných středisek ukazuje známý přiřazený mezisoučet. Nepřiřazené náklady nelze přiřadit k vybranému středisku, proto jsou jeho souhrnné PNO a zisk neúplné."}) : null} sortKey="marketing" sort={channelTableSort} onSort={toggleChannelTableSort} align="right"/>
                                    <SortableHeader label={t("reporting_lab.table.revenue", {defaultValue: "Obrat"})} sortKey="revenue" sort={channelTableSort} onSort={toggleChannelTableSort} align="right"/>
                                    {hasProfitField && <SortableHeader label={t("reporting_lab.table.contribution", {defaultValue: "Hrubý zisk po reklamě"})} tooltip={<>{t("reporting_lab.table.contribution_help", {defaultValue: "Hrubý zisk po reklamě = hrubý zisk minus náklady na reklamní média. Jde o částku před dalšími variabilními náklady a režií; u produktů jsou náklady na reklamu analyticky alokované."})}<p>{t("reporting_lab.partial_profitability.channel_help", {defaultValue: "Pokud chybí nákupní ceny, jde o dílčí výsledek z řádků se známou tržbou a nákupním nákladem; odečítají se všechny reklamní náklady stejného výběru."})}</p>{metricAvailability.channelSubtotal && <p>{t("reporting_lab.centre_spend_attribution.selected", {defaultValue: "Výkon vybraných středisek ukazuje známý přiřazený mezisoučet. Nepřiřazené náklady nelze přiřadit k vybranému středisku, proto jsou jeho souhrnné PNO a zisk neúplné."})}</p>}</>} sortKey="profit" sort={channelTableSort} onSort={toggleChannelTableSort} align="right"/>}
                                    <SortableHeader label="PNO" tooltip={metricAvailability.selectedCentreAttributionIncomplete ? t("reporting_lab.centre_spend_attribution.selected", {defaultValue: "Výkon vybraných středisek ukazuje známý přiřazený mezisoučet. Nepřiřazené náklady nelze přiřadit k vybranému středisku, proto jsou jeho souhrnné PNO a zisk neúplné."}) : t("reporting_lab.metrics_help.pno", {defaultValue: "Marketingové náklady jako podíl obratu."})} sortKey="pno" sort={channelTableSort} onSort={toggleChannelTableSort} align="right"/>
                                    <SortableHeader label={t("reporting_lab.table.orders_short", {defaultValue: "Obj."})} sortKey="orders" sort={channelTableSort} onSort={toggleChannelTableSort} align="right"/>
                                </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                {visibleChannelRows.map((row) => <tr key={row.channel.id}>
                                    <td className="py-3 pr-4 font-semibold text-slate-900 dark:text-slate-100">{channelRowName(row, sortedChannels, t)}</td>
                                    <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">
                                        {(() => {
                                            const spend = channelSpendPresentation(row);
                                            return <>{renderCurrencyValue(spend.value, true)}{spend.partial && <span className="ml-1 text-xs font-normal text-slate-500 dark:text-slate-400">{t("reporting_lab.marketing.partial_spend", {defaultValue: i18n.language?.startsWith("cs") ? "Dílčí částka" : "Partial amount"})}</span>}</>;
                                        })()}
                                    </td>
                                    <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(row.turnover, true)}</td>
                                    {hasProfitField && <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{row.profit == null ? "—" : renderCurrencyValue(row.profit, true)}</td>}
                                    <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{metricAvailability.selectedCentreAttributionIncomplete ? "—" : renderPercentValue(row.pno)}</td>
                                    <td className="py-3 text-right text-slate-600 dark:text-slate-300">{renderNumber(row.orders)}</td>
                                </tr>)}
                                </tbody>
                            </table>
                        </div>
                        <TablePagination page={safeChannelPage} pageSize={channelLimit} totalItems={sortedTableChannels.length} onPageChange={setChannelPage} onPageSizeChange={(value) => { setChannelLimit(value); setChannelPage(1); }} pageSizeOptions={[5, 10, 25, 50]} t={t}/>
                    </Panel>

                    {metricAvailability.channelReview && <Panel loading={loading} title={t("reporting_lab.panels.channels_to_review", {defaultValue: "Kanály k revizi"})} className="col-span-12 xl:col-span-4">
                        <div className="space-y-3">
                            {riskyChannels.map((row) => <div key={row.channel.id} className="app-surface rounded-lg border border-slate-200 p-3 dark:border-slate-700">
                                <div className="flex items-center justify-between gap-3">
                                    <div className="font-semibold text-slate-900 dark:text-slate-100">{channelRowName(row, sortedChannels, t)}</div>
                                    <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">{renderPercentValue(row.pno)}</div>
                                </div>
                                <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-slate-500 dark:text-slate-400">
                                    <div>
                                        {t("reporting_lab.review.spend", {defaultValue: "Útrata:"})}{" "}
                                        {channelSpendPresentation(row).value?.amount == null
                                            ? "—"
                                            : <strong className={`font-semibold ${moneyAmount(row.spendMarketing) < 0 ? "text-datixo-negative" : "text-slate-700 dark:text-slate-200"}`}>
                                                {renderCurrencyValue(channelSpendPresentation(row).value, true)}{channelSpendPresentation(row).partial && <span className="ml-1 text-xs font-normal text-slate-500 dark:text-slate-400">{t("reporting_lab.marketing.partial_spend", {defaultValue: i18n.language?.startsWith("cs") ? "Dílčí částka" : "Partial amount"})}</span>}
                                            </strong>}
                                    </div>
                                    {hasProfitField && <div className="text-right">
                                        {t("reporting_lab.review.profit", {defaultValue: "Hrubý zisk po reklamě:"})}{" "}
                                        {row.profit?.amount == null
                                            ? "—"
                                            : <strong className={`font-semibold ${moneyAmount(row.profit) < 0 ? "text-datixo-negative" : "text-slate-700 dark:text-slate-200"}`}>{renderCurrencyValue(row.profit, true)}</strong>}
                                    </div>}
                                </div>
                            </div>)}
                            {riskyChannels.length === 0 && <div className="text-sm text-slate-500 dark:text-slate-400">{t("reporting_lab.review.empty", {defaultValue: "Pro vybrané období nejsou dostupná marketingová data k revizi."})}</div>}
                        </div>
                    </Panel>}
                </div>
        </div>
    </WideSecureLayout>;
}
